import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr, dateStr } from "@/lib/format";
import { KPI, SectionHeader } from "@/components/ui-extras";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Users, Calendar, Wallet, IndianRupee, Plus, CheckCircle2, Clock, X, Download } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

function PayslipButton({ employeeId, month, empCode }) {
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/payslip/${employeeId}/${month}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error("Failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Payslip_${empCode}_${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Payslip download failed"); }
    finally { setLoading(false); }
  };
  return (
    <Button size="sm" variant="outline" onClick={download} disabled={loading}
      data-testid={`payslip-${employeeId}`} className="h-7 text-xs gap-1">
      <Download className="h-3 w-3" /> {loading ? "…" : "Payslip"}
    </Button>
  );
}

export default function Payroll() {
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [payroll, setPayroll] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedEmp, setSelectedEmp] = useState(null);

  const load = async () => {
    const [e, a, l, p] = await Promise.all([
      api.get("/employees"),
      api.get(`/attendance?month=${month}`),
      api.get("/leaves"),
      api.get(`/payroll/${month}`),
    ]);
    setEmployees(e.data); setAttendance(a.data); setLeaves(l.data); setPayroll(p.data);
    if (e.data.length && !selectedEmp) setSelectedEmp(e.data[0]);
  };
  useEffect(() => { load(); }, [month]);

  const totalCTC = employees.reduce((s, e) => s + Number(e.ctc || 0), 0);
  const presentToday = attendance.filter((a) => a.date === new Date().toISOString().slice(0, 10) && a.status === "present").length;

  return (
    <div className="space-y-6" data-testid="payroll-root">
      <SectionHeader
        title="Payroll & Attendance"
        subtitle="Employee master, attendance calendar, and monthly payroll with PF/ESI/TDS"
        right={
          <div className="flex gap-2 items-center">
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="payroll-month"
              className="h-9 w-40"
            />
            <NewEmployeeDialog onCreated={load} />
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI testId="hr-employees" label="Active Employees" value={employees.length} icon={Users} />
        <KPI testId="hr-present" label="Present Today" value={presentToday} icon={CheckCircle2} tone="good" />
        <KPI testId="hr-ctc" label="Annual CTC" value={totalCTC} compact icon={Wallet} />
        <KPI testId="hr-ot" label={`OT roll-up · ${month}`} value={payroll?.total_ot || 0} compact icon={Clock} tone={payroll?.total_ot ? "warn" : "muted"} />
        <KPI testId="hr-payout" label={`Payout · ${month}`} value={payroll?.total_net || 0} compact icon={IndianRupee} tone="warn" />
      </div>

      <Card className="shadow-none border-secondary/30 bg-secondary/5">
        <CardContent className="p-3 text-xs leading-relaxed text-muted-foreground">
          <span className="text-secondary font-mono uppercase text-[10px] tracking-wide mr-2">OT roll-up</span>
          Monthly overtime logged in <b>Site Execution &amp; Fab → Overtime</b> or via the <b>Digital Timesheet</b> is auto-folded here.
          <span className="mx-1 font-mono">PF</span> stays on basic only, <span className="font-mono">ESI</span> is charged on (base + OT) when gross ≤ ₹21k,
          and <span className="font-mono">TDS</span> is computed on the total incl. OT. The payslip PDF prints a full OT breakdown.
        </CardContent>
      </Card>

      <Tabs defaultValue="payroll">
        <TabsList>
          <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll · {month}</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">Employees ({employees.length})</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="tab-leaves">Leave requests</TabsTrigger>
        </TabsList>

        <TabsContent value="payroll" className="mt-4">
          <Card className="shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold font-display">Monthly payroll · {month}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                    <th className="py-2 font-medium">Emp</th>
                    <th className="py-2 font-medium">Name</th>
                    <th className="py-2 font-medium">Dept</th>
                    <th className="py-2 font-medium text-right">Paid days</th>
                    <th className="py-2 font-medium text-right">Basic</th>
                    <th className="py-2 font-medium text-right">HRA</th>
                    <th className="py-2 font-medium text-right">Allow.</th>
                    <th className="py-2 font-medium text-right">Base gross</th>
                    <th className="py-2 font-medium text-right">OT (hrs · ₹)</th>
                    <th className="py-2 font-medium text-right">Gross</th>
                    <th className="py-2 font-medium text-right">PF</th>
                    <th className="py-2 font-medium text-right">ESI</th>
                    <th className="py-2 font-medium text-right">TDS</th>
                    <th className="py-2 font-medium text-right">Net</th>
                    <th className="py-2 font-medium">Payslip</th>
                  </tr>
                </thead>
                <tbody>
                  {(payroll?.rows || []).map((r) => (
                    <tr key={r.employee_id} className="border-b hover:bg-accent/50" data-testid={`payroll-row-${r.employee_id}`}>
                      <td className="py-2 font-mono text-xs">{r.emp_code}</td>
                      <td className="py-2">{r.name}</td>
                      <td className="py-2"><Badge variant="outline">{r.department}</Badge></td>
                      <td className="py-2 cell-num">{r.paid_days}</td>
                      <td className="py-2 cell-num text-muted-foreground">{inr(r.basic, { compact: true })}</td>
                      <td className="py-2 cell-num text-muted-foreground">{inr(r.hra, { compact: true })}</td>
                      <td className="py-2 cell-num text-muted-foreground">{inr(r.allowances, { compact: true })}</td>
                      <td className="py-2 cell-num text-muted-foreground">{inr(r.base_gross, { compact: true })}</td>
                      <td className="py-2 cell-num" data-testid={`payroll-ot-${r.employee_id}`}>
                        {r.ot_pay > 0 ? (
                          <span className="text-secondary-foreground">
                            <span className="font-mono text-[10px] text-muted-foreground mr-1">{r.ot_hours}h</span>
                            +{inr(r.ot_pay, { compact: true })}
                          </span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="py-2 cell-num font-medium">{inr(r.gross, { compact: true })}</td>
                      <td className="py-2 cell-num text-destructive">-{inr(r.pf, { compact: true })}</td>
                      <td className="py-2 cell-num text-destructive">-{inr(r.esi, { compact: true })}</td>
                      <td className="py-2 cell-num text-destructive">-{inr(r.tds, { compact: true })}</td>
                      <td className="py-2 cell-num font-semibold text-primary">{inr(r.net, { compact: true })}</td>
                      <td className="py-2">
                        <PayslipButton employeeId={r.employee_id} month={month} empCode={r.emp_code} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-primary/20">
                    <td colSpan={8} className="py-2 text-right text-xs uppercase font-mono text-muted-foreground">Total OT · Net payout</td>
                    <td className="py-2 cell-num font-bold text-secondary-foreground" data-testid="payroll-total-ot">+{inr(payroll?.total_ot || 0, { compact: true })}</td>
                    <td colSpan={4} />
                    <td className="py-2 cell-num font-bold text-primary" data-testid="payroll-total-net">{inr(payroll?.total_net || 0, { compact: true })}</td>
                    <td/>
                  </tr>
                </tfoot>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employees" className="mt-4">
          <Card className="shadow-none">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono bg-muted/40">
                    <th className="p-3 font-medium">Code</th>
                    <th className="p-3 font-medium">Name</th>
                    <th className="p-3 font-medium">Role</th>
                    <th className="p-3 font-medium">Dept</th>
                    <th className="p-3 font-medium">PAN</th>
                    <th className="p-3 font-medium text-right">CTC</th>
                    <th className="p-3 font-medium">PF / ESI</th>
                    <th className="p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((e) => (
                    <tr key={e.employee_id} className="border-b hover:bg-accent/50">
                      <td className="p-3 font-mono text-xs">{e.emp_code}</td>
                      <td className="p-3 font-medium">{e.name}</td>
                      <td className="p-3">{e.role}</td>
                      <td className="p-3"><Badge variant="outline">{e.department}</Badge></td>
                      <td className="p-3 font-mono text-xs">{e.pan}</td>
                      <td className="p-3 cell-num">{inr(e.ctc, { compact: true })}</td>
                      <td className="p-3 text-xs">
                        {e.pf_applicable && <Badge variant="outline" className="mr-1 border-secondary/60 text-secondary-foreground">PF</Badge>}
                        {e.esi_applicable && <Badge variant="outline">ESI</Badge>}
                      </td>
                      <td className="p-3"><Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Active</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attendance" className="mt-4">
          <Card className="shadow-none">
            <CardHeader className="pb-3 flex-row items-center gap-3 justify-between">
              <CardTitle className="text-sm font-semibold font-display">Attendance · {month}</CardTitle>
              <div className="flex gap-2 items-center">
                <Select value={selectedEmp?.employee_id || ""} onValueChange={(v) => setSelectedEmp(employees.find((e) => e.employee_id === v))}>
                  <SelectTrigger className="w-56 h-9"><SelectValue placeholder="Employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.employee_id} value={e.employee_id}>{e.emp_code} · {e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <MarkAttendanceDialog employees={employees} onCreated={load} />
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <AttendanceCalendar
                month={month}
                records={attendance.filter((a) => !selectedEmp || a.employee_id === selectedEmp.employee_id)}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaves" className="mt-4">
          <Card className="shadow-none">
            <CardHeader className="pb-3 flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold font-display">Leave requests</CardTitle>
              <NewLeaveDialog employees={employees} onCreated={load} />
            </CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                    <th className="py-2 font-medium">Employee</th>
                    <th className="py-2 font-medium">Type</th>
                    <th className="py-2 font-medium">From</th>
                    <th className="py-2 font-medium">To</th>
                    <th className="py-2 font-medium">Reason</th>
                    <th className="py-2 font-medium">Status</th>
                    <th className="py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leaves.map((l) => {
                    const emp = employees.find((e) => e.employee_id === l.employee_id);
                    return (
                      <tr key={l.leave_id} className="border-b hover:bg-accent/50">
                        <td className="py-2">{emp?.name || "—"}</td>
                        <td className="py-2"><Badge variant="outline">{l.leave_type}</Badge></td>
                        <td className="py-2 text-xs">{dateStr(l.from_date)}</td>
                        <td className="py-2 text-xs">{dateStr(l.to_date)}</td>
                        <td className="py-2 max-w-[220px] truncate">{l.reason}</td>
                        <td className="py-2">
                          <Badge
                            className={
                              l.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                              l.status === "rejected" ? "bg-red-100 text-red-800 border-red-200" :
                              "bg-amber-100 text-amber-800 border-amber-200"
                            }
                          >{l.status}</Badge>
                        </td>
                        <td className="py-2">
                          {l.status === "pending" && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" data-testid={`approve-${l.leave_id}`} className="h-7 text-xs" onClick={async () => {
                                await api.patch(`/leaves/${l.leave_id}`, { status: "approved" });
                                toast.success("Leave approved");
                                load();
                              }}><CheckCircle2 className="h-3 w-3 mr-1" />Approve</Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
                                await api.patch(`/leaves/${l.leave_id}`, { status: "rejected" });
                                toast.error("Leave rejected");
                                load();
                              }}><X className="h-3 w-3 mr-1" />Reject</Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {leaves.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No leave requests</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AttendanceCalendar({ month, records }) {
  const [y, m] = month.split("-").map(Number);
  const firstDay = new Date(y, m - 1, 1).getDay();
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${month}-${String(d).padStart(2, "0")}`;
    const day = records.find((r) => r.date === iso);
    cells.push({ day: d, iso, status: day?.status });
  }
  const colorMap = {
    present: "bg-emerald-100 text-emerald-800 border-emerald-300",
    absent: "bg-red-100 text-red-800 border-red-300",
    half_day: "bg-amber-100 text-amber-800 border-amber-300",
    leave: "bg-sky-100 text-sky-800 border-sky-300",
    holiday: "bg-muted text-muted-foreground border-border",
  };
  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] font-mono uppercase text-muted-foreground text-center">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => (
          <div key={i} className={`aspect-square rounded border text-xs flex items-start justify-between p-1.5 ${c?.status ? colorMap[c.status] : "bg-card border-border"}`}>
            {c && (
              <>
                <span className="font-medium">{c.day}</span>
                {c.status && <span className="text-[9px] uppercase">{c.status.slice(0,3)}</span>}
              </>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-3 text-xs">
        {Object.entries(colorMap).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded ${v}`}/>{k}</div>
        ))}
      </div>
    </div>
  );
}

function NewEmployeeDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", emp_code: "", role: "", department: "Operations",
    doj: new Date().toISOString().slice(0, 10), email: "", phone: "",
    ctc: 0, pf_applicable: true, esi_applicable: false, pan: "", bank_account: "", status: "active",
  });
  const save = async () => {
    const basic = form.ctc * 0.4 / 12;
    const hra = basic * 0.5;
    const allow = form.ctc / 12 - basic - hra;
    try {
      await api.post("/employees", { ...form, ctc: Number(form.ctc), basic, hra, allowances: allow });
      toast.success("Employee added");
      setOpen(false); onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-employee-btn" className="h-9 bg-primary hover:bg-primary/90"><Plus className="h-4 w-4 mr-1" />New employee</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></Field>
          <Field label="Emp Code"><Input value={form.emp_code} onChange={(e) => setForm({...form, emp_code: e.target.value})} /></Field>
          <Field label="Role"><Input value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} /></Field>
          <Field label="Department">
            <Select value={form.department} onValueChange={(v) => setForm({...form, department: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Operations","Finance","HR","Sales","Admin"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="DOJ"><Input type="date" value={form.doj} onChange={(e) => setForm({...form, doj: e.target.value})} /></Field>
          <Field label="Email"><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></Field>
          <Field label="PAN"><Input value={form.pan} onChange={(e) => setForm({...form, pan: e.target.value})} /></Field>
          <Field label="CTC (annual ₹)"><Input type="number" value={form.ctc} onChange={(e) => setForm({...form, ctc: e.target.value})} /></Field>
          <Field label="Bank A/C"><Input value={form.bank_account} onChange={(e) => setForm({...form, bank_account: e.target.value})} /></Field>
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2"><Switch checked={form.pf_applicable} onCheckedChange={(v) => setForm({...form, pf_applicable: v})} /><Label>PF applicable</Label></div>
            <div className="flex items-center gap-2"><Switch checked={form.esi_applicable} onCheckedChange={(v) => setForm({...form, esi_applicable: v})} /><Label>ESI applicable</Label></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkAttendanceDialog({ employees, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: "", date: new Date().toISOString().slice(0, 10), status: "present", check_in: "09:00", check_out: "18:00",
  });
  const save = async () => {
    if (!form.employee_id) return toast.error("Select employee");
    try { await api.post("/attendance", form); toast.success("Marked"); setOpen(false); onCreated(); }
    catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline"><Plus className="h-3 w-3 mr-1" />Mark</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Mark attendance</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Employee" full>
            <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
              <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.emp_code} · {e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["present","absent","half_day","leave","holiday"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewLeaveDialog({ employees, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: "", from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10), leave_type: "casual", reason: "", status: "pending",
  });
  const save = async () => {
    if (!form.employee_id || !form.reason) return toast.error("Fill fields");
    try { await api.post("/leaves", form); toast.success("Leave applied"); setOpen(false); onCreated(); }
    catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="new-leave-btn"><Plus className="h-3 w-3 mr-1" />Apply leave</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Apply leave</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Employee" full>
            <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
              <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.emp_code} · {e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="From"><Input type="date" value={form.from_date} onChange={(e) => setForm({...form, from_date: e.target.value})} /></Field>
          <Field label="To"><Input type="date" value={form.to_date} onChange={(e) => setForm({...form, to_date: e.target.value})} /></Field>
          <Field label="Type">
            <Select value={form.leave_type} onValueChange={(v) => setForm({...form, leave_type: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["casual","sick","earned","unpaid"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reason" full><Textarea rows={2} value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} /></Field>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90">Apply</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
